import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./TradeEntryAttemptLogTable.css";
import { buildScopedApiUrl, ensureArray, getApiHeaders } from "./config";
import { useWorkspace } from "./workspace/WorkspaceContext";
import TradingViewChartIcon from "./TradingViewChartIcon";

const BASE_SERVICE_URL = "/api/bot/ui/get";

function formatDate(value) {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
}

function display(value) {
  return value === undefined || value === null || value === "" ? "-" : value;
}

function TradeEntrySkipLogTable() {
  const { workspace } = useWorkspace();
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [reasonCode, setReasonCode] = useState("ALL");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "createdDate", direction: "desc" });

  const fetchLogs = useCallback(() => {
    setLoading(true);
    setError("");

    fetch(buildScopedApiUrl(`${BASE_SERVICE_URL}/getTradeEntrySkipLogs`, workspace), {
      headers: getApiHeaders(),
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Request failed with status ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        const normalized = ensureArray(data).map((row) => ({
          ...row,
          exchange: row.exchange || workspace.exchange,
          marketType: row.marketType || workspace.marketType,
        }));
        setLogs(normalized);
        setPage(1);
      })
      .catch((err) => {
        console.error("Trade entry skip log fetch failed:", err);
        setError("Unable to load trade entry skip logs right now.");
        setLogs([]);
      })
      .finally(() => setLoading(false));
  }, [workspace]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const reasonCodes = useMemo(() => {
    const values = logs
      .map((row) => row.skipReasonCode)
      .filter((value) => value !== undefined && value !== null && value !== "");
    return ["ALL", ...Array.from(new Set(values)).sort()];
  }, [logs]);

  const filtered = useMemo(() => {
    const text = search.trim().toLowerCase();
    return logs.filter((row) => {
      const matchesReason = reasonCode === "ALL" || row.skipReasonCode === reasonCode;
      const matchesSearch =
        !text ||
        Object.values(row).some((value) => (value ?? "").toString().toLowerCase().includes(text));
      return matchesReason && matchesSearch;
    });
  }, [logs, reasonCode, search]);

  const sortedData = useMemo(() => {
    const sorted = [...filtered];
    const { key, direction } = sortConfig;

    sorted.sort((a, b) => {
      let aVal = a[key] ?? "";
      let bVal = b[key] ?? "";

      if (["id", "timeFrame", "relatedSignalRequestId", "tradeSetupRequestId", "tradeEntryTrackerId"].includes(key)) {
        aVal = Number(aVal || 0);
        bVal = Number(bVal || 0);
      }

      if (["createdDate", "modifiedDate"].includes(key)) {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      }

      if (aVal < bVal) return direction === "asc" ? -1 : 1;
      if (aVal > bVal) return direction === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filtered, sortConfig]);

  const totalPages = pageSize === "all" ? 1 : Math.max(1, Math.ceil(sortedData.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const offset = pageSize === "all" ? 0 : (currentPage - 1) * pageSize;
  const pagedData = pageSize === "all" ? sortedData : sortedData.slice(offset, offset + pageSize);

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  const handleSort = (key) => {
    setPage(1);
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  return (
    <div className="attempt-log-container">
      <h2>Trade Entry Skip Log</h2>

      <div className="toolbar">
        <input
          className="search-box"
          placeholder="Search symbol, reason, setup id..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />

        <select value={reasonCode} onChange={(e) => {
          setReasonCode(e.target.value);
          setPage(1);
        }}>
          {reasonCodes.map((code) => (
            <option key={code} value={code}>
              {code === "ALL" ? "All Reasons" : code}
            </option>
          ))}
        </select>

        <button className="btn-add" onClick={fetchLogs} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-wrapper">
        <table className="attempt-log-table">
          <thead>
            <tr>
              <th>S.No</th>
              <th onClick={() => handleSort("id")}>ID</th>
              <th onClick={() => handleSort("createdDate")}>Created</th>
              <th>Exchange</th>
              <th>Market Type</th>
              <th onClick={() => handleSort("symbol")}>Symbol</th>
              <th>Chart</th>
              <th onClick={() => handleSort("timeFrame")}>TF</th>
              <th>Side</th>
              <th>Source</th>
              <th onClick={() => handleSort("skipReasonCode")}>Reason Code</th>
              <th>Reason</th>
              <th onClick={() => handleSort("relatedSignalRequestId")}>Signal Request ID</th>
              <th onClick={() => handleSort("tradeSetupRequestId")}>Trade Setup ID</th>
              <th onClick={() => handleSort("tradeEntryTrackerId")}>Entry Tracker ID</th>
              <th>Active</th>
              <th>Created By</th>
            </tr>
          </thead>

          <tbody>
            {sortedData.length === 0 && (
              <tr>
                <td colSpan="17">{loading ? "Loading trade entry skip logs..." : "No trade entry skip logs found."}</td>
              </tr>
            )}

            {pagedData.map((row, index) => (
              <tr key={row.id}>
                <td>{offset + index + 1}</td>
                <td>{display(row.id)}</td>
                <td>{formatDate(row.createdDate)}</td>
                <td>{display(row.exchange)}</td>
                <td>{display(row.marketType)}</td>
                <td>{display(row.symbol)}</td>
                <td>
                  <TradingViewChartIcon
                    symbol={row.symbol}
                    timeFrame={row.timeFrame}
                    exchange={row.exchange}
                    marketType={row.marketType}
                  />
                </td>
                <td>{display(row.timeFrame)}</td>
                <td>{display(row.tradeSide)}</td>
                <td>{display(row.source)}</td>
                <td>{display(row.skipReasonCode)}</td>
                <td>{display(row.skipReason)}</td>
                <td>{display(row.relatedSignalRequestId)}</td>
                <td>{display(row.tradeSetupRequestId)}</td>
                <td>{display(row.tradeEntryTrackerId)}</td>
                <td>{display(row.activeFlag)}</td>
                <td>{display(row.createdBy)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="attempt-pagination">
        <div className="attempt-page-size">
          <label htmlFor="skip-log-page-size">Records per page</label>
          <select
            id="skip-log-page-size"
            value={pageSize}
            onChange={(event) => {
              setPageSize(event.target.value === "all" ? "all" : Number(event.target.value));
              setPage(1);
            }}
          >
            {[10, 20, 50, 100].map((size) => <option key={size} value={size}>{size}</option>)}
            <option value="all">All</option>
          </select>
        </div>
        <div className="attempt-page-navigation" aria-label="Trade entry skip log pagination">
          <button type="button" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}>
            Previous
          </button>
          <span>Page {currentPage} of {totalPages}</span>
          <button type="button" disabled={currentPage === totalPages} onClick={() => setPage(currentPage + 1)}>
            Next
          </button>
        </div>
        <div className="attempt-record-total">
          Showing {sortedData.length === 0 ? 0 : offset + 1}-{offset + pagedData.length} of {sortedData.length} records
          {filtered.length !== logs.length && ` (filtered from ${logs.length} total)`}
        </div>
      </div>
    </div>
  );
}

export default TradeEntrySkipLogTable;
